import { noOp } from 'common/Constants';
import { DEFAULT_DISH_RATING_FILTER } from 'common/DishRatingFilter';
import { ClientEvents } from 'common/events/ClientEvents';
import { DateString } from 'common/UrlDate';
import { QueryParams } from 'common/urls/QueryParams';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import { DishFirstSegmentFilter, useConsumerExploreDishFirstFiltersQuery } from 'src/gqlReactTypings.generated.d';
import { useDishFirstExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { SpiceLevelMap } from 'src/shared/Enums';
import { useRefState } from 'src/shared/hooks/useRefState';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { DEFAULT_IS_ECO_FRIENDLY_PACKAGING, FilterUpdates } from '../../Constants';
import { getClearableQueryParamUpdate } from '../../utils';

const emptyArr = [];

type UseCachedFiltersProps = {
  onChangeFilters?: () => void;
  query?: string;
  shefId?: string;
};

export const useCachedDishFirstFilters = (props?: UseCachedFiltersProps) => {
  const match = useRouteMatch();
  const { isSmallScreen } = useScreenContext();
  const [, setTopOfPageRef] = useRefState<HTMLDivElement>();
  const tracker = useTracker();
  const { onChangeFilters = noOp, query = undefined, shefId = undefined } = props ?? {};

  const filters = useDishFirstExploreUrl({ match });
  const {
    activeZipCode: zipCode,
    dateFilters: availabilityDates,
    browseFilter,
    cuisineFilters,
    dietaryFilters,
    dishTypeFilters,
    ecoFilter: isEcoFriendlyPackaging,
    minRatingFilter: minDishRating,
    priceLevelFilters,
    proteinFilters,
    segmentFilters,
    spiceLevelFilters,
    tagFilters,
    updateQueryParams,
  } = filters;

  const [, setDeliveryDates] = usePersistentDeliveryDates();

  const { data, error, loading } = useConsumerExploreDishFirstFiltersQuery({
    variables: {
      zipCode: zipCode ?? '',
      filters: {
        availabilityDates,
        cuisineFilters,
        dietaryFilters,
        dishTypeFilters,
        spiceLevelFilters: spiceLevelFilters.map((value) => SpiceLevelMap[value]),
        proteinFilters,
        minDishRating,
        isEcoFriendlyPackaging,
        priceLevelFilters,
        segmentFilters,
        browseFilter,
        smallScreen: isSmallScreen,
        tagFilters,
      },
      query,
      shefId,
    },
  });

  const { data: noFilterData, error: noFilterError } = useConsumerExploreDishFirstFiltersQuery({
    variables: {
      zipCode: zipCode ?? '',
      filters: {
        availabilityDates,
        cuisineFilters,
      },
    },
    returnPartialData: true,
    fetchPolicy: 'cache-first',
  });

  const availableDeliveryDays = data?.exploreFoodItemsFilterOptions?.availableDeliveryDays ?? emptyArr;
  const availableCuisineCategories = data?.exploreFoodItemsFilterOptions?.availableCuisines ?? emptyArr;
  const allCuisineCategories = noFilterData?.exploreFoodItemsFilterOptions?.availableCuisines ?? emptyArr;
  const totalShefs = data?.exploreFoodItemsFilterOptions?.totalShefs;

  const handleDateChange = useCallback(
    (newDate?: DateString) => {
      setDeliveryDates(newDate ? [newDate] : []);

      const noDateSegmentFilters = filters.segmentFilters.filter(
        (segmentFilter) => segmentFilter !== DishFirstSegmentFilter.NextDay
      );
      if (filters.segmentFilters.length !== noDateSegmentFilters.length) {
        updateQueryParams({
          [QueryParams.SEGMENT_FILTER]: noDateSegmentFilters,
        });
      }
    },
    [filters.dateFilters.length, filters.segmentFilters, updateQueryParams, setDeliveryDates]
  );
  const areFiltersEmpty = useMemo(
    () =>
      isEmpty(filters.dietaryFilters) &&
      isEmpty(filters.dishTypeFilters) &&
      isEmpty(filters.spiceLevelFilters) &&
      isEmpty(filters.proteinFilters) &&
      isEmpty(filters.priceLevelFilters) &&
      !filters.ecoFilter,
    [filters]
  );

  const handleFiltersChange = useCallback(
    (updatedFilters: FilterUpdates) => {
      tracker.track(ClientEvents.SUBMIT_DISH_FILTER, updatedFilters);
      onChangeFilters();

      updateQueryParams({
        // Only update the browse filter if we're not on a see all page.
        ...(!updatedFilters.segmentFilters?.length && { [QueryParams.BROWSE_FILTER]: updatedFilters.browseFilter }),
        [QueryParams.CUISINE_FILTER]: updatedFilters.cuisineFilters,
        [QueryParams.DIETARY_FILTER]: updatedFilters.dietaryFilters,
        [QueryParams.DISH_TYPE_FILTER]: updatedFilters.dishTypeFilters,
        [QueryParams.ECO_FILTER]: getClearableQueryParamUpdate(
          updatedFilters.isEcoFriendlyPackaging,
          DEFAULT_IS_ECO_FRIENDLY_PACKAGING
        ),
        [QueryParams.MIN_RATING_FILTER]: getClearableQueryParamUpdate(
          updatedFilters.minRatingFilter,
          DEFAULT_DISH_RATING_FILTER
        ),
        [QueryParams.PRICE_LEVEL_FILTER]: updatedFilters.priceLevelFilters,
        [QueryParams.PROTEIN_FILTER]: updatedFilters.proteinFilters,
        [QueryParams.SEGMENT_FILTER]: updatedFilters.segmentFilters,
        [QueryParams.SPICE_LEVEL_FILTER]: updatedFilters.spiceLevelFilters,
      });
    },
    [onChangeFilters, tracker, updateQueryParams]
  );

  return {
    allCuisineCategories,
    availableDeliveryDays,
    availableCuisineCategories,
    totalShefs,
    filters,
    data,
    handleDateChange,
    handleFiltersChange,
    isFiltersLoading: loading,
    error: error ?? noFilterError,
    loading,
    setTopOfPageRef,
    areFiltersEmpty,
  };
};
